import React, { useState, useEffect, useCallback, useRef } from "react";
import { api, fmtNumber } from "@/lib/api";
import { TID } from "@/constants/tids";
import { logError } from "@/lib/logger";
import { rangeParams, RANGE_OPTIONS } from "@/lib/dateRanges";
import { chartColors } from "@/lib/chartTheme";
import { useNodeExport } from "@/hooks/useNodeExport";
import ActivityInsightsPrint from "@/components/activity/ActivityInsightsPrint";
import TabLoadError from "@/components/shared/TabLoadError";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Activity, Loader2, Sparkles, CalendarRange, TrendingUp, GitCompareArrows } from "lucide-react";
import { toast } from "sonner";

const REMARK_PLUGINS = [remarkGfm];
const TYPES = [
  { key: "calls", label: "Calls" },
  { key: "emails", label: "Emails" },
  { key: "tasks", label: "Tasks" },
  { key: "meetings", label: "Meetings" },
  { key: "notes", label: "Notes" },
];
const GRAN = [
  { value: "daily", label: "Day" },
  { value: "weekly", label: "Week" },
  { value: "monthly", label: "Month" },
];
const TREND_MARGIN = { left: 6, right: 10 };
const TREND_RADIUS = [3, 3, 0, 0];

function corrText(c) {
  if (c === null || c === undefined) return "not enough data (need 3+ reps with variance)";
  const s = c >= 0 ? "positive" : "negative";
  const strength = Math.abs(c) >= 0.7 ? "strong" : Math.abs(c) >= 0.4 ? "moderate" : "weak";
  return `${strength} ${s} (r = ${c})`;
}

export default function ActivityInsights({ range, onRangeChange, onRegisterExport, theme }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [granularity, setGranularity] = useState("monthly");
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const printRef = useRef(null);
  const c = chartColors(theme);
  const rangeLabel = RANGE_OPTIONS.find((o) => o.value === range)?.label || "All Time";

  const { exportPdf, printNode } = useNodeExport(
    printRef,
    () => !!(data && data.total > 0),
    "salestap-activity-insights"
  );

  useEffect(() => {
    if (!onRegisterExport) return;
    onRegisterExport("activity", { exportPdf, printFn: printNode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterExport, exportPdf, printNode, data]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { start, end } = rangeParams(range);
      const [ins, rep] = await Promise.all([
        api.getActivityInsights(start, end, granularity),
        api.getActivityReport().catch(() => ({ report_md: null })),
      ]);
      setData(ins);
      setReport(rep?.report_md || null);
    } catch (e) {
      logError("Failed to load activity insights:", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [range, granularity]);

  useEffect(() => {
    load();
  }, [load]);

  const generate = async () => {
    setReportLoading(true);
    try {
      const { start, end } = rangeParams(range);
      await api.generateActivityReport(start, end, granularity);
      const t0 = Date.now();
      const poll = async () => {
        const st = await api.activityReportStatus();
        if (st.status === "done") {
          setReport(st.report_md);
          setReportLoading(false);
          toast.success("Activity analysis complete.");
          return;
        }
        if (st.status === "error") {
          setReportLoading(false);
          toast.error(st.error || "Analysis failed.");
          return;
        }
        if (Date.now() - t0 > 180000) {
          setReportLoading(false);
          toast.error("Analysis timed out.");
          return;
        }
        setTimeout(poll, 3000);
      };
      setTimeout(poll, 3000);
    } catch (e) {
      setReportLoading(false);
      toast.error(e.response?.data?.detail || "Analysis failed.");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (loadError) {
    return <TabLoadError onRetry={load} />;
  }

  const total = data?.total || 0;
  const trend = data?.trend || [];
  const byType = data?.by_type || [];
  const byRep = data?.by_rep || [];
  const correlation = data?.correlation || { rows: [] };
  const countFor = (k) => byType.find((t) => t.type === k)?.count ?? 0;
  const tooltipStyle = {
    backgroundColor: theme === "dark" ? "#0d1526" : "#ffffff",
    border: `1px solid ${c.grid}`, borderRadius: 8,
    color: theme === "dark" ? "#fff" : "#0f172a", fontSize: 12,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-primary" />
          <div>
            <h2 className="font-semibold text-foreground leading-tight">Activity Insights</h2>
            <p className="text-xs text-muted-foreground">{fmtNumber(total)} activities · {rangeLabel}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Granularity toggle */}
          <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid={TID.activityGranularity}>
            {GRAN.map((g) => (
              <button
                key={g.value}
                onClick={() => setGranularity(g.value)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  granularity === g.value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <CalendarRange className="w-4 h-4 text-muted-foreground ml-1" />
          <Select value={range} onValueChange={onRangeChange}>
            <SelectTrigger className="w-[150px]" data-testid="activity-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid={TID.genActivityReportBtn} onClick={generate} disabled={reportLoading || total === 0} variant="outline" size="sm">
            {reportLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {report ? "Regenerate" : "AI Analysis"}
          </Button>
        </div>
      </div>

      {total === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          No activities in this period. (Activity data requires a live HubSpot connection with engagement read scopes.)
        </div>
      ) : (
        <>
          {/* Mix tiles */}
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
            {TYPES.map((t) => (
              <div key={t.key} className="rounded-lg border border-border bg-muted/40 px-3 py-3 text-center">
                <p className="text-xl font-bold text-foreground">{fmtNumber(countFor(t.key))}</p>
                <p className="text-[11px] text-muted-foreground">{t.label}</p>
              </div>
            ))}
          </div>

          {/* Trend chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" /> Activity Volume Over Time
                <span className="text-xs font-normal text-muted-foreground ml-1">· by {granularity.replace("ly", "")}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div data-testid={TID.activityTrendChart}>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={trend} margin={TREND_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={c.grid} />
                    <XAxis dataKey="label" fontSize={10} stroke={c.axis} />
                    <YAxis fontSize={11} stroke={c.axis} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend />
                    <Bar dataKey="calls" stackId="a" name="Calls" fill={c.primary} radius={TREND_RADIUS} />
                    <Bar dataKey="emails" stackId="a" name="Emails" fill="#64748b" />
                    <Bar dataKey="tasks" stackId="a" name="Tasks" fill="#d97706" />
                    <Bar dataKey="meetings" stackId="a" name="Meetings" fill={c.won} />
                    <Bar dataKey="notes" stackId="a" name="Notes" fill="#94a3b8" radius={TREND_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Per-rep leaderboard + correlation — each on its own full-width row */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Activity className="w-4 h-4 text-primary" /> Activity by Rep</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-[300px] overflow-y-auto overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rep</TableHead>
                        {TYPES.map((t) => <TableHead key={t.key} className="text-right">{t.label}</TableHead>)}
                        <TableHead className="text-right font-semibold">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {byRep.map((r) => (
                        <TableRow key={r.owner_id}>
                          <TableCell className="font-medium text-foreground">{r.name}</TableCell>
                          {TYPES.map((t) => <TableCell key={t.key} className="text-right text-muted-foreground">{fmtNumber(r[t.key] ?? 0)}</TableCell>)}
                          <TableCell className="text-right font-semibold">{fmtNumber(r.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><GitCompareArrows className="w-4 h-4 text-primary" /> Activity ↔ Outcomes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-xs text-muted-foreground">
                  <p>Activities vs. wins: <span className="font-medium text-foreground">{corrText(correlation.activities_vs_wins)}</span></p>
                  <p>Meetings vs. wins: <span className="font-medium text-foreground">{corrText(correlation.meetings_vs_wins)}</span></p>
                </div>
                <div className="max-h-[220px] overflow-y-auto overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Rep</TableHead>
                        <TableHead className="text-right">Activities</TableHead>
                        <TableHead className="text-right">Meetings</TableHead>
                        <TableHead className="text-right">Won</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(correlation.rows || []).map((r) => (
                        <TableRow key={r.owner_id}>
                          <TableCell className="font-medium text-foreground">{r.name}</TableCell>
                          <TableCell className="text-right">{fmtNumber(r.activities)}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{fmtNumber(r.meetings)}</TableCell>
                          <TableCell className="text-right">{fmtNumber(r.won_count)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>

          {report && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" /> AI Activity Analysis</CardTitle>
              </CardHeader>
              <CardContent>
                <article data-testid={TID.activityReportContent} className="prose prose-slate dark:prose-invert max-w-none prose-h2:text-lg prose-strong:text-foreground">
                  <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{report}</ReactMarkdown>
                </article>
              </CardContent>
            </Card>
          )}
        </>
      )}

      <ActivityInsightsPrint ref={printRef} insights={data} reportMd={report} rangeLabel={rangeLabel} />
    </div>
  );
}
